import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GenericModalService } from '../../../shared/generic-modal/generic-modal.service';
import { MODAL_DATA } from '../../../shared/tokens/modal-data.token';
import { TranslationService } from '../../../shared/translation.service';
import { WindowService } from '../../../shared/window.service';
import { CohortTrackEvents } from '../../../shared/types/cohort-track-events.enum';
import { CohortResetEligibilityDto } from '../cohort-reset-eligibility.dto';
import { CohortResetService } from '../cohort-reset.service';
import { GTMService } from '../../../shared/gtm.service';

export interface CohortResetModalData {
  eligibility: CohortResetEligibilityDto | null;
}

@Component({
  selector: 'lp2-cohort-reset-modal',
  templateUrl: './cohort-reset-modal.component.html',
  styleUrls: ['./cohort-reset-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CohortResetModalComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  remainingAttempts: number;
  allowedAttempts: number;
  checkboxIrreversible = false;
  checkboxLimit = false;
  isResetting = false;

  constructor(
    @Inject(MODAL_DATA) private data: CohortResetModalData,
    private modal: NgbActiveModal,
    private cohortResetService: CohortResetService,
    private genericModalService: GenericModalService,
    private windowService: WindowService,
    private translationService: TranslationService,
    private gtmService: GTMService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    const eligibility = this.data.eligibility;
    if (eligibility && eligibility.eligibleForReset) {
      this.remainingAttempts = eligibility.allowedAttempts - eligibility.attempts;
      this.allowedAttempts = eligibility.allowedAttempts;
    }
  }

  get canConfirm(): boolean {
    return this.checkboxIrreversible && this.checkboxLimit;
  }

  get showResetContent(): boolean {
    return !this.isLimitReached;
  }

  get isLimitReached(): boolean {
    return !this.data.eligibility || !this.data.eligibility.eligibleForReset;
  }

  get attemptsRemainingText(): string {
    return this.translationService.translate(
      'oeapp.studybundle.cohort.change.modal.attemptsRemaining',
      { remainingAttempts: this.remainingAttempts, allowedAttempts: this.allowedAttempts }
    );
  }

  onConfirm() {
    this.isResetting = true;
    this.cdr.markForCheck();
    this.gtmService.sendGA4Event(CohortTrackEvents.COHORT_CHANGE_CONFIRMED);

    this.cohortResetService
      .resetCohort$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.gtmService.sendGA4Event(CohortTrackEvents.COHORT_CHANGE_SUCCESS);
          this.modal.close();
          this.windowService.redirectToHref('/');
        },
        () => {
          this.isResetting = false;
          this.genericModalService.show(
            this.translationService.translate('alert.titleError'),
            this.translationService.translate('errors.unablePerformAction')
          );
          this.cdr.markForCheck();
        }
      );
  }

  onDismiss() {
    this.modal.dismiss();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}



// WEBPACK FOOTER //
// ./ts/app/home/cohort/cohort-reset-modal/cohort-reset-modal.component.ts